import { create } from 'zustand';
import { themes, getTheme, DEFAULT_THEME_ID } from '@/lib/themes.js';

/** Migrate legacy 'light' / 'dark' values to the new default IDs. */
function migrate(stored) {
  if (stored === 'light') return 'ocean-light';
  if (stored === 'dark')  return 'ocean-dark';
  return stored;
}

/** Inject a theme's CSS variable overrides and update <html> attributes. */
function applyTheme(theme) {
  let styleEl = document.getElementById('app-theme-vars');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'app-theme-vars';
    document.head.appendChild(styleEl);
  }
  const declarations = Object.entries(theme.vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  styleEl.textContent = `:root {\n${declarations}\n}`;

  document.documentElement.dataset.theme = theme.id;
  document.documentElement.classList.toggle('dark', theme.mode === 'dark');
}

// ─── Initialize before React mounts ─────────────────────────────────────────
const storedRaw    = (typeof window !== 'undefined' && localStorage.getItem('theme')) || DEFAULT_THEME_ID;
const initialId    = migrate(storedRaw);
const initialTheme = getTheme(initialId);
if (typeof window !== 'undefined') applyTheme(initialTheme);

// ─── Store ───────────────────────────────────────────────────────────────────
export const useThemeStore = create((set, get) => ({
  themeId: initialTheme.id,
  /** 'light' | 'dark' — compat alias for AppShell's `theme === 'dark'` checks */
  theme: initialTheme.mode,
  /** Same as theme, for explicitness */
  mode: initialTheme.mode,

  setTheme(id) {
    const next = getTheme(id);
    localStorage.setItem('theme', next.id);
    applyTheme(next);
    set({ themeId: next.id, theme: next.mode, mode: next.mode });
  },

  /** Flip between the light and dark variant of the current palette. */
  toggle() {
    const current = getTheme(get().themeId);
    const targetMode = current.mode === 'dark' ? 'light' : 'dark';
    const pair = themes.find((t) => t.group === current.group && t.mode === targetMode);
    const fallback = targetMode === 'light' ? DEFAULT_THEME_ID : 'ocean-dark';
    get().setTheme(pair ? pair.id : fallback);
  },
}));
